import React, { useEffect, useState, useCallback } from "react";
import API from "../config";
import usePermissions from "../hooks/usePermissions";

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  role_id: "",
  company_id: "",
  device_ids: [],        // multi-select allotted devices
  co2_device_ids: [],
};

const APP_ADMIN_ROLES = ["super_admin", "system_manager"];

/* ─────────────────────────────────────────────
   Users Page
───────────────────────────────────────────── */
const Users = () => {
  const token    = localStorage.getItem("token");
  // Parse the logged-in user stored at login (expect { id, role, company_id, ... })
  const me       = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
  const roleName = String(me.role || "").toLowerCase().replace(/\s+/g, "_");
  const isPlatformAdmin = ["super_admin", "system_manager", "editor"].includes(roleName);
  const isSuperAdmin = roleName === "super_admin";

  const { canCreate, canDelete, canEdit, canView } = usePermissions();

  /* ── state ── */
  const [users,       setUsers]       = useState([]);
  const [companies,   setCompanies]   = useState([]);
  const [roles,       setRoles]       = useState([]);
  const [devices,     setDevices]     = useState([]);   // scoped by selected company
  const [co2Devices,  setCo2Devices]  = useState([]);

  const [showModal,   setShowModal]   = useState(false);
  const [editMode,    setEditMode]    = useState(false);   // false = create, true = edit
  const [editId,      setEditId]      = useState(null);

  const [form,        setForm]        = useState(EMPTY_FORM);
  const [loading,     setLoading]     = useState(false);
  const [fetchLoading,setFetchLoading]= useState(true);
  const [error,       setError]       = useState("");

  /* ─────────────────────────────────────────
     Fetch helpers
  ───────────────────────────────────────── */
  const fetchUsers = useCallback(async () => {
    try {
      setFetchLoading(true);
      const res  = await fetch(`${API}/users?scope=company`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setFetchLoading(false);
    }
  }, [token]);

  const fetchCompanies = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      // Company user sees only their own company
      setCompanies(isPlatformAdmin ? data : data.filter((c) => c.id === me.company_id));
    } catch (err) {
      console.error(err);
    }
  }, [token, isPlatformAdmin, me.company_id]);

  const fetchRoles = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/roles`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      setRoles((Array.isArray(data) ? data : []).filter((role) => {
        const name = String(role.name || "").toLowerCase().replace(/\s+/g, "_");
        return !APP_ADMIN_ROLES.includes(name);
      }));
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  /* Fetch devices scoped to the company currently chosen in the form */
  const fetchDevices = useCallback(async (companyId) => {
    if (!companyId) { setDevices([]); return; }
    try {
      const res  = await fetch(`${API}/reports/devices?company_id=${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  const fetchCo2Devices = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await fetch("/api/co2/admin/devices", { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      setCo2Devices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  }, [token, isSuperAdmin]);

  /* ── initial load ── */
  useEffect(() => {
    if (!token) return;
    fetchUsers();
    fetchCompanies();
    fetchRoles();
    fetchCo2Devices();
  }, [token, fetchUsers, fetchCompanies, fetchRoles, fetchCo2Devices]);

  /* ── when company changes in form, reload devices ── */
  useEffect(() => {
    fetchDevices(form.company_id);
  }, [form.company_id, fetchDevices]);

  /* ─────────────────────────────────────────
     Open modal helpers
  ───────────────────────────────────────── */
  const openCreate = () => {
    setEditMode(false);
    setEditId(null);
    setError("");
    setForm({
      ...EMPTY_FORM,
      // Pre-fill company for non-admin users and lock it
      company_id: isPlatformAdmin ? "" : String(me.company_id),
    });
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditMode(true);
    setEditId(user.id);
    setError("");
    setForm({
      name:       user.name       || "",
      email:      user.email      || "",
      password:   "",                     // never pre-fill password
      role_id:    String(user.role_id  || ""),
      company_id: String(user.company_id || ""),
      device_ids: user.device_ids ? user.device_ids.map(String) : [],
      co2_device_ids: user.co2_device_ids ? user.co2_device_ids.map(String) : [],
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
    setError("");
  };

  /* ─────────────────────────────────────────
     Form field helper
  ───────────────────────────────────────── */
  const field = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const toggleDevice = (id) => {
    setForm((prev) => ({
      ...prev,
      device_ids: prev.device_ids.includes(id)
        ? prev.device_ids.filter((d) => d !== id)
        : [...prev.device_ids, id],
    }));
  };

  const toggleCo2Device = (id) => {
    setForm((prev) => ({
      ...prev,
      co2_device_ids: prev.co2_device_ids.includes(id)
        ? prev.co2_device_ids.filter((deviceId) => deviceId !== id)
        : [...prev.co2_device_ids, id],
    }));
  };

  const selectedRoleName = String(roles.find((role) => String(role.id) === String(form.role_id))?.name || "")
    .toLowerCase().replace(/\s+/g, "_");
  const isCompanyAdminTarget = selectedRoleName === "company_admin";

  /* ─────────────────────────────────────────
     CREATE
  ───────────────────────────────────────── */
  const handleCreate = async () => {
    setError("");
    if (!form.name || !form.email || !form.password || !form.role_id || !form.company_id) {
      setError("All fields are required");
      return;
    }
    try {
      setLoading(true);
      const res  = await fetch(`${API}/users`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Create failed"); return; }
      closeModal();
      fetchUsers();
    } catch {
      setError("Server error");
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────
     EDIT / UPDATE
  ───────────────────────────────────────── */
  const handleUpdate = async () => {
    setError("");
    if (!form.name || !form.email || !form.role_id || !form.company_id) {
      setError("Name, email, role, and company are required");
      return;
    }
    try {
      setLoading(true);
      // Only send password if the admin actually typed a new one
      const payload = { ...form };
      if (!payload.password) delete payload.password;

      const res  = await fetch(`${API}/users/${editId}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Update failed"); return; }
      closeModal();
      fetchUsers();
    } catch {
      setError("Server error");
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────
     DELETE
  ───────────────────────────────────────── */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      const res = await fetch(`${API}/users/${id}`, {
        method:  "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  /* ─────────────────────────────────────────
     Guards
  ───────────────────────────────────────── */
  if (!canView("users"))  return <div className="p-6 text-red-500">Access Denied</div>;
  if (fetchLoading)       return <div className="p-6 text-slate-500">Loading...</div>;

  /* ─────────────────────────────────────────
     Render
  ───────────────────────────────────────── */
  return (
    <div className="p-6">

      {/* ── Header ── */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Company Users</h1>
          <p className="text-sm text-slate-400 mt-1">Manage company-level users and their device access.</p>
        </div>
        {canCreate("users") && (
          <button
            onClick={openCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition"
          >
            + Add Company User
          </button>
        )}
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="px-6 py-3 text-left">Name</th>
              <th className="px-6 py-3 text-left">Email</th>
              <th className="px-6 py-3 text-left">Role</th>
              <th className="px-6 py-3 text-left">Company</th>
              <th className="px-6 py-3 text-left">Devices</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-slate-400">
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-t hover:bg-slate-50 transition">
                  <td className="px-6 py-4 font-medium text-slate-800">{u.name}</td>
                  <td className="px-6 py-4 text-slate-500">{u.email}</td>
                  <td className="px-6 py-4">
                    <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">
                      {u.role_name || u.role_id}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-500">{u.company_name || u.company_id}</td>
                  <td className="px-6 py-4 text-slate-500 text-xs">
                    {u.device_ids && u.device_ids.length > 0
                      ? u.device_ids.join(", ")
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-6 py-4 text-right space-x-3">
                    {canEdit("users") && (
                      <button
                        onClick={() => openEdit(u)}
                        className="text-blue-500 hover:text-blue-700 text-sm font-medium transition"
                      >
                        Edit
                      </button>
                    )}
                    {canDelete("users") && (
                      <button
                        onClick={() => handleDelete(u.id)}
                        className="text-red-500 hover:text-red-700 text-sm font-medium transition"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">

            {/* Modal Header */}
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-semibold text-slate-800">
                {editMode ? "Edit Company User" : "Create Company User"}
              </h2>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-2 rounded-lg">
                {error}
              </div>
            )}

            {/* Name */}
            <label className="block text-xs text-slate-500 mb-1 font-medium">Name *</label>
            <input
              placeholder="Full name"
              className="w-full border border-slate-200 rounded-lg p-2.5 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={form.name}
              onChange={field("name")}
            />

            {/* Email */}
            <label className="block text-xs text-slate-500 mb-1 font-medium">Email *</label>
            <input
              type="email"
              placeholder="user@example.com"
              className="w-full border border-slate-200 rounded-lg p-2.5 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={form.email}
              onChange={field("email")}
            />

            {/* Password */}
            <label className="block text-xs text-slate-500 mb-1 font-medium">
              Password {editMode && <span className="text-slate-400">(leave blank to keep current)</span>}
            </label>
            <input
              type="password"
              placeholder={editMode ? "New password (optional)" : "Password"}
              className="w-full border border-slate-200 rounded-lg p-2.5 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={form.password}
              onChange={field("password")}
            />

            {/* Role */}
            <label className="block text-xs text-slate-500 mb-1 font-medium">Role *</label>
            <select
              className="w-full border border-slate-200 rounded-lg p-2.5 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
              value={form.role_id}
              onChange={(event) => {
                const roleId = event.target.value;
                const selected = roles.find((role) => String(role.id) === roleId);
                const selectedName = String(selected?.name || "").toLowerCase().replace(/\s+/g, "_");
                setForm((current) => ({
                  ...current,
                  role_id: roleId,
                  co2_device_ids: selectedName === "company_admin" ? [] : current.co2_device_ids,
                }));
              }}
            >
              <option value="">Select Role</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>

            {/* Company — locked for non-admins */}
            <label className="block text-xs text-slate-500 mb-1 font-medium">Company *</label>
            <select
              className={`w-full border border-slate-200 rounded-lg p-2.5 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white ${
                !isPlatformAdmin ? "opacity-60 cursor-not-allowed" : ""
              }`}
              value={form.company_id}
              onChange={field("company_id")}
              disabled={!isPlatformAdmin}
            >
              <option value="">Select Company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>

            {/* Devices — multi-select, only shown when a company is picked */}
            {form.company_id && (
              <>
                <label className="block text-xs text-slate-500 mb-1 font-medium">
                  Allotted Devices
                  <span className="text-slate-400 font-normal ml-1">(select all that apply)</span>
                </label>
                {devices.length === 0 ? (
                  <p className="text-xs text-slate-400 mb-3">No devices found for this company.</p>
                ) : (
                  <div className="border border-slate-200 rounded-lg p-3 mb-3 max-h-36 overflow-y-auto space-y-2">
                    {devices.map((d) => {
                      const id    = String(d.device_id || d.id);
                      const label = d.device_name || d.name || id;
                      return (
                        <label key={id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.device_ids.includes(id)}
                            onChange={() => toggleDevice(id)}
                            className="accent-blue-600"
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {isSuperAdmin && !isCompanyAdminTarget && (
              <>
                <label className="block text-xs text-slate-500 mb-1 font-medium">
                  Allotted CO2 Devices
                  <span className="text-slate-400 font-normal ml-1">(only these users can view them)</span>
                </label>
                {co2Devices.length === 0 ? (
                  <p className="text-xs text-slate-400 mb-3">No CO2 devices configured.</p>
                ) : (
                  <div className="border border-slate-200 rounded-lg p-3 mb-3 max-h-36 overflow-y-auto space-y-2">
                    {co2Devices.map((device) => {
                      const id = String(device.id);
                      return (
                        <label key={id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" checked={form.co2_device_ids.includes(id)} onChange={() => toggleCo2Device(id)} className="accent-blue-600" />
                          <span>{device.name} <span className="text-slate-400 font-mono text-xs">{device.deviceUid}</span></span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-2">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={editMode ? handleUpdate : handleCreate}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition disabled:opacity-60"
              >
                {loading
                  ? editMode ? "Saving..." : "Creating..."
                  : editMode ? "Save Changes" : "Create User"}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
